/**
 * Which pages mint a session at idle, before the visitor does anything.
 *
 * Minting loads Cloudflare's Turnstile and, for a visitor it is unsure about,
 * puts its "tick the box" card on the page. So only pages whose next step needs
 * a session mint early: the landing page's link box and the job page's polling.
 * Anything else that ever needs one mints it on demand (`lib/api.ts`).
 *
 * THE TRANSCRIPT AND FORMAT PAGES ARE NOT HERE. Their one API call,
 * GET /api/transcripts/{id}, has been anonymous since contract api.md 1.12.1,
 * and they were left on this list after it: a visitor opening a shared
 * transcript link was shown the Turnstile card over the transcript, for a
 * token the page never spends (found 2026-10-17).
 *
 * A converter tool page never calls the API at all, so minting there would put
 * a third-party request on a page whose claim is that nothing leaves the
 * browser.
 *
 * THE SIX AUDIO TOOL PAGES DO MINT EARLY (/audio-to-text and its siblings,
 * marked `data-tool-input="audio"` by tools/tool.html). They are upload pages:
 * the meter they show reads GET /api/quota and the upload is
 * POST /api/transcribe/upload, and both need a session. The meter would mint
 * one on demand at load anyway, so minting here changes no request; it makes
 * the decision explicit and keeps it if the meter ever moves. Any Turnstile
 * check then happens while the visitor is still finding the file, not after
 * they press "Transcribe it". The trust line on those pages already says the
 * audio goes to our server, so nothing on them claims otherwise. Every other
 * tool page carries no marker and still never loads Turnstile.
 *
 * SO DOES A TOOL PAGE WITH THE HOMEPAGE'S PASTE BOX (/video-to-text,
 * SEO_BRIEF_04 item 14), for the homepage's reason: its next step may be a
 * link, which needs a session, and the meter beside the box reads the quota.
 *
 * AND THE AUTO SUBTITLE GENERATOR (`autosub`, item 14), whose one job starts
 * with an upload and whose meter reads the quota, like the audio pages.
 *
 * NOT THE SUBTITLE TRANSLATOR (`subtitle-translate`, SEO_BRIEF_09 section 2.3). Its one request,
 * the lines of text to translate, waits for the visitor to press Translate; a visitor who only
 * reads the page, or drops a file to look at it, loads no Turnstile. The press mints on demand.
 *
 * NOT THE LIVE VOICE TRANSLATOR (`live-translate`, SEO_BRIEF_10). A visitor who only reads the
 * page loads no Turnstile; a tap on a microphone mints the session while the visitor starts
 * talking (lib/live-translator/core.ts `warm`), unless the device translates the pair itself.
 *
 * NOT THE CALCULATORS (`calculator`, SEO_BRIEF_11 section 2.1). They ask our server nothing.
 *
 * A MICROPHONE PAGE (`mic`, SEO_BRIEF_08) ONLY WHEN RECORD OPENS FIRST
 * (/voice-to-text, /record-and-transcribe, /online-voice-recorder): its meter
 * is on screen and its next step may be Transcribe. A Live-first page
 * (/speech-to-text, /voice-typing) does not: live typing asks our server
 * nothing, and opening Record reads the meter, which mints on demand.
 */

export type PageName =
  | 'landing'
  | 'job'
  | 'transcript'
  | 'format'
  | 'tool'
  | 'editor'
  | 'autosub'
  | 'mic'
  | 'text-pdf'
  | 'subtitle-translate'
  | 'live-translate'
  | 'calculator'
  | 'static';

export const NEEDS_SESSION: ReadonlySet<PageName> = new Set<PageName>(['landing', 'job']);

/** The marker an audio tool page carries on its `[data-tool-page]` article. */
export const AUDIO_TOOL_SELECTOR = '[data-tool-input="audio"]';

/** The homepage's paste box, on a tool page that carries one. */
export const LINK_BOX_SELECTOR = '[data-transcribe-form]';

/**
 * Whether this page mints a session at idle: the list above, an audio tool page, a tool page
 * with the paste box, or the auto subtitle generator.
 */
/** A microphone panel that opens on Record. */
export const RECORD_FIRST_SELECTOR = '[data-mic-panel][data-mic-open="record"]';

export function mintsEarly(page: PageName, root: Pick<ParentNode, 'querySelector'>): boolean {
  if (NEEDS_SESSION.has(page) || page === 'autosub') return true;
  if (page === 'mic') return root.querySelector(RECORD_FIRST_SELECTOR) !== null;
  if (page !== 'tool') return false;
  return (
    root.querySelector(AUDIO_TOOL_SELECTOR) !== null ||
    root.querySelector(LINK_BOX_SELECTOR) !== null
  );
}
